import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt, hhmm } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Crown, Medal, Award, Trophy } from "lucide-react";

const RANK_ICON = { 1: Crown, 2: Medal, 3: Award };
const SELECT = "mt-2 w-full rounded-none border border-input bg-secondary px-3 py-2 text-sm";

export const PodiumAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const [live, setLive] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmAgain, setConfirmAgain] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/podium"),
        api.get("/podium"),
        api.get("/admin/rewards"),
      ]);
      setCfg(a.data);
      setLive(b.data);
      setRewards(c.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/podium", {
        period: cfg.period,
        metric: cfg.metric,
        announce: cfg.announce || "",
        prizes: cfg.prizes.map((p) => ({
          rank: p.rank,
          label: p.label || "",
          points: Number(p.points) || 0,
          reward_id: p.reward_id || "",
        })),
      });
      toast.success("Podium saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const award = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/podium/award${confirmAgain ? "?force=true" : ""}`);
      toast.success(`Prizes paid out to ${data.awarded.length} player(s)`);
      setConfirmAgain(false);
      load();
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("already paid out")) {
        setConfirmAgain(true);
        toast.error("Already paid out this period — click again to pay anyway.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const setPrize = (rank, field, value) =>
    setCfg({
      ...cfg,
      prizes: cfg.prizes.map((p) => (p.rank === rank ? { ...p, [field]: value } : p)),
    });

  if (!cfg || !live) return <p className="text-sm text-muted-foreground">Loading podium…</p>;

  return (
    <div data-testid="podium-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg md:text-lg font-semibold">Leaderboard podium & prizes</h2>
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-none" onClick={save} data-testid="save-podium-btn">
            Save config
          </Button>
          <Button className="rounded-none" onClick={award} disabled={busy} data-testid="award-podium-btn">
            <Trophy className="mr-2 h-4 w-4" />
            {confirmAgain ? "Pay out again (confirm)" : "Pay out prizes now"}
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-3" data-testid="podium-standings">
        {[1, 2, 3].map((rank) => {
          const s = live.standings.find((x) => x.rank === rank);
          const prize = cfg.prizes.find((p) => p.rank === rank);
          const Icon = RANK_ICON[rank];
          return (
            <div
              key={rank}
              data-testid={`podium-card-${rank}`}
              className={`border p-6 ${rank === 1 ? "border-primary/60 bg-primary/5" : "border-border bg-card"}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-6 w-6 ${rank === 1 ? "text-primary" : "text-muted-foreground"}`} />
                <span className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground">#{rank}</span>
              </div>
              <div className="mt-4 text-lg font-semibold">{s ? s.username : "— no one yet —"}</div>
              <div className="mono mt-1 text-sm text-primary">
                {s ? `${hhmm(s.minutes)} played · ${fmt(s.points)} pts` : "awaiting playtime"}
              </div>
              <div className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">
                Prize: {prize?.label || "unnamed"} · {fmt(prize?.points || 0)} pts
                {prize?.reward_id ? " + item" : ""}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div className="border border-border bg-card p-8 md:col-span-2" data-testid="podium-config">
          <h3 className="text-lg font-semibold">Configure</h3>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Reset period</Label>
              <select
                className={SELECT}
                data-testid="podium-period"
                value={cfg.period}
                onChange={(e) => setCfg({ ...cfg, period: e.target.value })}
              >
                <option value="weekly">Weekly (resets Monday)</option>
                <option value="monthly">Monthly (resets 1st)</option>
                <option value="alltime">All-time</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ranked by</Label>
              <select
                className={SELECT}
                data-testid="podium-metric"
                value={cfg.metric}
                onChange={(e) => setCfg({ ...cfg, metric: e.target.value })}
              >
                <option value="minutes">Most played (minutes)</option>
                <option value="points">Most points earned</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Announcement shown to players
              </Label>
              <Textarea
                className="mt-2 rounded-none bg-secondary"
                data-testid="podium-announce"
                value={cfg.announce || ""}
                onChange={(e) => setCfg({ ...cfg, announce: e.target.value })}
              />
            </div>
          </div>

          <h4 className="mt-8 text-xs uppercase tracking-[0.2em] text-muted-foreground">Prizes</h4>
          <div className="mt-4 space-y-4">
            {cfg.prizes
              .slice()
              .sort((a, b) => a.rank - b.rank)
              .map((p) => (
                <div key={p.rank} className="grid grid-cols-12 items-end gap-3" data-testid={`prize-row-${p.rank}`}>
                  <span className="mono col-span-1 pb-3 text-primary">#{p.rank}</span>
                  <div className="col-span-4">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Label</Label>
                    <Input
                      className="mt-2 rounded-none bg-secondary"
                      data-testid={`prize-label-${p.rank}`}
                      value={p.label || ""}
                      onChange={(e) => setPrize(p.rank, "label", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bonus points</Label>
                    <Input
                      type="number"
                      className="mt-2 rounded-none bg-secondary mono"
                      data-testid={`prize-points-${p.rank}`}
                      value={p.points ?? 0}
                      onChange={(e) => setPrize(p.rank, "points", e.target.value)}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Item prize (optional)
                    </Label>
                    <select
                      className={SELECT}
                      data-testid={`prize-reward-${p.rank}`}
                      value={p.reward_id || ""}
                      onChange={(e) => setPrize(p.rank, "reward_id", e.target.value)}
                    >
                      <option value="">No item</option>
                      {rewards.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Paying out grants the bonus points instantly and drops any item prize into the redemption queue
            (free of charge) so it can be delivered in-game with <span className="mono">/claim</span>. Weekly
            periods reset Monday 00:00 UTC, monthly on the 1st. Each period can only be paid out once unless
            you confirm a repeat.
          </p>
        </div>

        <div className="border border-border bg-card p-6" data-testid="podium-history">
          <h3 className="text-lg font-semibold">Payout history</h3>
          <div className="mt-4 space-y-4 text-sm">
            {(!cfg.history || cfg.history.length === 0) && (
              <p className="text-muted-foreground">No prizes paid out yet.</p>
            )}
            {(cfg.history || []).map((h) => (
              <div key={h.id} className="border-b border-border pb-3" data-testid={`podium-history-${h.id}`}>
                <div className="flex items-center justify-between">
                  <Badge className="rounded-none uppercase" variant="secondary">
                    {h.period}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(h.created_at).toLocaleDateString()}
                  </span>
                </div>
                {h.winners.map((w) => (
                  <div key={w.rank} className="mt-2 flex justify-between text-xs">
                    <span>
                      #{w.rank} {w.username}
                    </span>
                    <span className="mono text-primary">
                      +{fmt(w.points_awarded)}
                      {w.reward_name ? " + item" : ""}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
